/**
 * Where a pupil's mistakes fall — the reading side of `marks`.
 *
 * A teacher touching a word in a live session writes one mark: surah, ayah,
 * word. Until App Review's fifth refusal (2026-10-01, "we were unable to
 * locate تصحيح تلاوة") no screen read any of them back, so a correction
 * existed only for the seconds it was on the shared page. This turns the
 * marks into places, and counts the ones that come back — the "أين تكرر
 * خطؤه" the listing promises a guardian.
 *
 * Pure, so it is the same on both clients and needs no device to test.
 */

/** 114 surahs; anything else is not a place in the Quran. */
const SURAHS = 114;

export type MarkPlace = { surah: number; ayah: number; word: number };
export type Mark = MarkPlace & { at: number };
export type Mistake = MarkPlace & { count: number; lastAt: number };

const isPlace = (m: Partial<Mark>): m is Mark =>
  Number.isInteger(m.surah) && (m.surah as number) >= 1 && (m.surah as number) <= SURAHS
  && Number.isInteger(m.ayah) && (m.ayah as number) >= 1
  && Number.isInteger(m.word) && (m.word as number) >= 0;

/**
 * One row per word, most repeated first, and among equals the most recent —
 * a mistake made three times is the one worth practising, and of two made
 * once, the newer is the one still live.
 */
export function groupMistakes(marks: readonly Partial<Mark>[]): Mistake[] {
  const byPlace = new Map<string, Mistake>();
  for (const m of marks) {
    if (!isPlace(m)) continue;
    const key = `${m.surah}:${m.ayah}:${m.word}`;
    const at = Number.isFinite(m.at) ? m.at : 0;
    const held = byPlace.get(key);
    if (held) {
      held.count += 1;
      held.lastAt = Math.max(held.lastAt, at);
    } else {
      byPlace.set(key, { surah: m.surah, ayah: m.ayah, word: m.word, count: 1, lastAt: at });
    }
  }
  return [...byPlace.values()].sort((a, b) => b.count - a.count || b.lastAt - a.lastAt);
}

/**
 * The word a mark points at, from the text the app ships.
 *
 * A word index past the end of its ayah names nothing, and says so with
 * null: the place is still worth showing, and a guessed word would put a
 * mistake on a word the child read correctly.
 */
export function describeMistake(
  place: MarkPlace,
  wordsOf: (surah: number, ayah: number) => string[],
): { word: string | null } {
  const words = wordsOf(place.surah, place.ayah);
  return { word: words[place.word] ?? null };
}
